"use client";

import { useState, useCallback, useEffect, useRef, lazy, Suspense } from "react";
import { Routes, Route, useNavigate, useParams, Navigate, useLocation } from "react-router-dom";
import "./styles/App.css";
import "./styles/components/ui/AppHeader.css";
import Sidebar from "./components/sidebar/Sidebar";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import UserMenu from "./components/ui/UserMenu";
import TopProgressBar from "./components/ui/TopProgressBar";
import { useTheme } from './contexts/ThemeContext';
import { RxHamburgerMenu } from "react-icons/rx";

/* Modals lazy-loaded: només es carreguen quan l'usuari els obre */
const ConfirmDeleteModal = lazy(() => import("./components/modals/ConfirmDeleteModal"));
const RenameModal = lazy(() => import("./components/modals/RenameModal"));
const ChatSearchModal = lazy(() => import("./components/modals/ChatSearchModal"));
const PinnedMessagesModal = lazy(() => import("./components/modals/PinnedMessagesModal"));

const ChatPage = lazy(() => import("./pages/ChatPage"));
const NewChatPage = lazy(() => import("./pages/NewChatPage"));
const Onboarding = lazy(() => import("./Onboarding"));
const LoginPage = lazy(() => import("./auth/LoginPage"));

const VerifyEmailPage = lazy(() => import("./auth/VerifyEmailPage"));
const ForgotPasswordPage = lazy(() => import("./auth/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./auth/ResetPasswordPage"));
const ChangeEmailPage = lazy(() => import("./auth/ChangeEmailPage"));
const ChangePasswordPage = lazy(() => import("./auth/ChangePasswordPage"));
const TwoFactorPage = lazy(() => import("./auth/TwoFactorPage"));
const ConfirmEmailChangePage = lazy(() => import("./auth/ConfirmEmailChangePage"));
const VerifyTwoFactorPage = lazy(() => import('./auth/VerifyTwoFactorPage'));
const RecoverAccountPage = lazy(() => import("./auth/RecoverAccountPage"));
const RecoverChatsPage = lazy(() => import("./auth/RecoverChatsPage"));
const PerfilPage = lazy(() => import('./pages/PerfilPage'));

export function ThemeToggleButton() {
  const { theme, toggleTheme } = useTheme();

  return (
    <button
      className="theme-toggle-btn"
      onClick={toggleTheme}
      aria-label="Canviar tema"
      title={theme === 'light' ? 'Modo fosc' : 'Modo clar'}
    >
      <div className={`theme-icon-wrapper ${theme}`}>
        {theme === 'light' ? (
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="5" />
            <line x1="12" y1="1" x2="12" y2="3" />
            <line x1="12" y1="21" x2="12" y2="23" />
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
            <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
            <line x1="1" y1="12" x2="3" y2="12" />
            <line x1="21" y1="12" x2="23" y2="12" />
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
            <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
          </svg>
        )}
      </div>
    </button>
  );
}

/**
 * Llegeix la llista de xats des de localforage de manera asíncrona.
 * 
 * @returns {Promise<Array>} La llista de xats
 */
async function loadChatListDB() {
  try {
    const localforage = (await import('localforage')).default;
    const saved = await localforage.getItem('chat_list');
    return saved || [];
  } catch { return []; }
}

/**
 * Desa la llista de xats a localforage.
 * 
 * @param {Array} list - La llista de xats a desar.
 */
async function saveChatListDB(list) {
  try {
    const localforage = (await import('localforage')).default;
    await localforage.setItem('chat_list', list);
  } catch { }
}


/**
 * Component estructural que engloba l'aplicació sencera (sidebar + contingut principal).
 * També gestiona l'animació de col·lapse/expansió de la barra lateral i allotja modals
 * com el de cerca o renombrar xat.
 * 
 * @param {Object} props - Totes les propietats passades des d'AppInner.
 */
function Layout({ children, chats, activeChatId, onNewChat, onDeleteChat, onRenameChat, onStarChat, selectedModel, onModelChange, showOnboarding, onOnboardingDone, onStartTutorial, settingsOpen, setSettingsOpen, username, role, pfpUrl, onLogout, sidebarOpen, setSidebarOpen, searchOpen, setSearchOpen, deletingId, onConfirmDeleteRequest }) {
  const navigate = useNavigate();
  const [renameChat, setRenameChat] = useState(null);
  const [flashTitleId, setFlashTitleId] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [pinnedModalOpen, setPinnedModalOpen] = useState(false);
  const sidebarRef = useRef(null);
  const mainRef = useRef(null);
  const isAnimating = useRef(false);
  const SIDEBAR_W = 220;

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const currentChat = activeChatId ? chats.find(c => c.id === activeChatId) : null;
  const currentTitle = currentChat ? currentChat.title : null;

  const handleRenameWithFlash = (id, title) => {
    setFlashTitleId(id);
    onRenameChat(id, title);
    setTimeout(() => setFlashTitleId(null), 400);
  };

  return (
    <div className="app-container">
      <div className="app-card">
        <Sidebar
          sidebarRef={sidebarRef}
          isDesktopCollapsed={sidebarCollapsed}
          onToggleSidebar={toggleSidebar}
          chats={chats}
          activeChatId={activeChatId}
          onNewChat={onNewChat}
          onSelect={(id) => { navigate(`/chat/${id}`); setSidebarOpen(false); }}
          onDeleteChat={onDeleteChat}
          onConfirmDeleteRequest={onConfirmDeleteRequest}
          onRenameRequest={(chat) => setRenameChat(chat)}
          onStarChat={onStarChat}
          selectedModel={selectedModel}
          onModelChange={onModelChange}
          showOnboarding={showOnboarding}
          settingsOpen={settingsOpen}
          setSettingsOpen={setSettingsOpen}
          onStartTutorial={onStartTutorial}
          onSearchOpen={() => setSearchOpen(true)}
          deletingId={deletingId}
          flashTitleId={flashTitleId}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />

        <main ref={mainRef} className="app-main">
          <div className="app-header">
            <div className="header-side">
              {/* Mobile: toggle overlay sidebar */}
              <button className="hamburger-btn mobile-only" onClick={() => setSidebarOpen(o => !o)} aria-label="Menú">
                <RxHamburgerMenu size={20} />
              </button>
              {/* Desktop: collapse/expand sidebar */}
              <button className="sidebar-toggle-btn desktop-only" onClick={toggleSidebar} aria-label={sidebarCollapsed ? 'Mostra barra lateral' : 'Amaga barra lateral'} title={sidebarCollapsed ? 'Mostra barra lateral' : 'Amaga barra lateral'}>
                <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  style={{ transform: sidebarCollapsed ? 'scaleX(-1)' : 'scaleX(1)', transition: 'transform 0.3s ease' }}>
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <line x1="9" y1="3" x2="9" y2="21" />
                  <polyline points="15 9 12 12 15 15" />
                </svg>
              </button>
              {currentTitle && (
                <div className="app-header-chat-title" title={currentTitle}>
                  {currentTitle}
                </div>
              )}
            </div>
            <button className="app-title" onClick={() => navigate('/new')}>EbrenIA</button>
            <div className="header-side header-side--right" style={{ gap: '12px', alignItems: 'center' }}>
              <ThemeToggleButton />
              <UserMenu username={username} role={role} pfpUrl={pfpUrl} onLogout={onLogout} onStartTutorial={onStartTutorial} onOpenPinned={() => setPinnedModalOpen(true)} />
            </div>
          </div>
          <div className="speech-component">
            {children}
          </div>
        </main>
      </div>

      {showOnboarding && <Onboarding
        onDone={onOnboardingDone}
        settingsOpen={settingsOpen}
        setSidebarOpen={setSidebarOpen}
        sidebarCollapsed={sidebarCollapsed}
        toggleSidebar={toggleSidebar}
      />}

      {searchOpen && (
        <Suspense fallback={null}>
          <ChatSearchModal
            chats={chats}
            favourites={new Set(chats.filter(c => c.starred).map(c => c.id))}
            onSelect={(id) => { setSearchOpen(false); navigate(`/chat/${id}`); }}
            onClose={() => setSearchOpen(false)}
          />
        </Suspense>
      )}
      {pinnedModalOpen && (
        <Suspense fallback={null}>
          <PinnedMessagesModal
            onSelect={(chatId) => navigate(`/chat/${chatId}`)}
            onClose={() => setPinnedModalOpen(false)}
          />
        </Suspense>
      )}
      {renameChat && (
        <Suspense fallback={null}>
          <RenameModal
            currentTitle={renameChat.title}
            onConfirm={(title) => { if (title.trim()) handleRenameWithFlash(renameChat.id, title.trim()); setRenameChat(null); }}
            onCancel={() => setRenameChat(null)}
          />
        </Suspense>
      )}
    </div>
  );
}

/**
 * Component arrel (root). Proveïdor de context d'autenticació i encaminador
 * (Router) principal. Defineix les rutes tant privades com públiques (auth).
 */
const App = () => (
  <AuthProvider>
    <TopProgressBar />
    <Suspense fallback={<TopProgressBar />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/account/change-email" element={<ChangeEmailPage />} />
        <Route path="/account/change-password" element={<ChangePasswordPage />} />
        <Route path="/account/2fa" element={<TwoFactorPage />} />
        <Route path="/confirm-email-change" element={<ConfirmEmailChangePage />} />
        <Route path="/verify-2fa" element={<VerifyTwoFactorPage />} />
        <Route path="/recover-account" element={<RecoverAccountPage />} />
        <Route path="/recover-chats" element={<RecoverChatsPage />} />
        <Route path="*" element={<AppInner />} />
      </Routes>
    </Suspense>
  </AuthProvider>
);

/**
 * Sub-component que executa la lògica interna (API calls, estats) un cop
 * l'usuari està autenticat, i defineix les rutes privades de l'aplicació.
 */
function AppInner() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuth, username, logout, role, pfpUrl } = useAuth();
  const [chats, setChats] = useState([]);

  useEffect(() => {
    // Netejar completament el localStorage antic
    localStorage.clear();
    
    // Carregar caché inicial des d'IndexedDB
    loadChatListDB().then(saved => {
      setChats(prev => prev.length === 0 && saved.length > 0 ? saved : prev);
    });
  }, []);

  useEffect(() => {
    const KONAMI = ['ArrowUp', 'ArrowUp', 'ArrowDown', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'ArrowLeft', 'ArrowRight', 'b', 'a'];
    let seq = [];
    const handler = (e) => {
      seq.push(e.key);
      if (seq.length > KONAMI.length) seq.shift();
      if (seq.join(',') === KONAMI.join(',')) {
        seq = [];
        fetch('/api/dev/reload-lexic', { method: 'POST' }).catch(() => { });
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  useEffect(() => {
    if (!isAuth) return;
    fetch('/api/chats', { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(dbChats => {
        const formatted = dbChats.map(c => ({ id: c.id, title: c.title, starred: !!c.starred, model: c.model || 'gpt-4o', createdAt: new Date(c.created_at).getTime() }));
        setChats(formatted);
        saveChatListDB(formatted);
      })
      .catch(() => { });
  }, [isAuth]);

  const [selectedModel, setSelectedModel] = useState('gpt-4o');
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingReady, setOnboardingReady] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    if (!isAuth) return;
    fetch('/api/me', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) setShowOnboarding(!data.onboarding_done);
        setOnboardingReady(true);
      })
      .catch(() => setOnboardingReady(true));
  }, [isAuth]);

  const activeChatId = location.pathname.startsWith('/chat/')
    ? location.pathname.replace('/chat/', '')
    : null;

  const activeChat = chats.find(c => String(c.id) === activeChatId);
  const currentModel = activeChat ? (activeChat.model || 'gpt-4o') : selectedModel;

  const updateChatTitle = useCallback((chatId, title) => {
    setChats(prev => {
      const updated = prev.map(c => c.id === chatId ? { ...c, title } : c);
      saveChatListDB(updated);
      return updated;
    });
    if (isAuth) {
      fetch(`/api/chats/${chatId}/title`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', },
        body: JSON.stringify({ title })
      }).catch(() => { });
    }
  }, [isAuth]);

  const handleStarChat = useCallback((chatId) => {
    setChats(prev => {
      const updated = prev.map(c => c.id === chatId ? { ...c, starred: !c.starred } : c);
      saveChatListDB(updated);
      return updated;
    });
    if (isAuth) {
      fetch(`/api/chats/${chatId}/star`, {
        method: 'PATCH',
        headers: {}
      }).catch(() => { });
    }
  }, [isAuth]);

  const handleChatCreated = useCallback((newChatId, title) => {
    const newChat = { id: newChatId, title: title || 'Nou xat', model: currentModel, createdAt: Date.now() };
    setChats(prev => {
      const updated = [newChat, ...prev];
      saveChatListDB(updated);
      return updated;
    });
  }, []);

  const deleteChat = useCallback((chatId) => {
    const updated = chats.filter(c => String(c.id) !== String(chatId));
    saveChatListDB(updated);
    setChats(updated);
    if (String(activeChatId) === String(chatId)) navigate(updated.length > 0 ? `/chat/${updated[0].id}` : '/new');
    if (isAuth) {
      fetch(`/api/chats/${chatId}`, { method: 'DELETE', headers: {} }).catch(() => { });
    }
  }, [chats, activeChatId, isAuth, navigate]);

  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const handleDeleteWithFade = useCallback((id) => {
    setDeletingId(id);
    setTimeout(() => {
      deleteChat(id);
      setDeletingId(null);
      setConfirmDeleteId(null);
    }, 300);
  }, [deleteChat]);

  const handleModelChange = (modelId) => {
    if (activeChatId) {
      setChats(prev => {
        const updated = prev.map(c => String(c.id) === activeChatId ? { ...c, model: modelId } : c);
        saveChatListDB(updated);
        return updated;
      });
      fetch(`/api/chats/${activeChatId}/model`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ model: modelId })
      }).catch(() => { });
    } else {
      setSelectedModel(modelId);
    }
  };

  const handleOnboardingDone = () => {
    setShowOnboarding(false);
    setSettingsOpen(false);
    if (isAuth) {
      fetch('/api/me/onboarding', { method: 'PATCH', headers: {} }).catch(() => { });
    }
  };

  const sharedProps = {
    chats,
    activeChatId,
    onNewChat: () => navigate('/new'),
    onDeleteChat: deleteChat,
    onRenameChat: updateChatTitle,
    selectedModel: currentModel,
    onModelChange: handleModelChange,
    showOnboarding: onboardingReady && showOnboarding,
    onOnboardingDone: handleOnboardingDone,
    onStartTutorial: () => { setSettingsOpen(false); setShowOnboarding(true); },
    settingsOpen,
    setSettingsOpen,
    username,
    role,
    onLogout: () => { logout(); navigate('/login', { replace: true }); },
    onStarChat: handleStarChat,
    sidebarOpen,
    setSidebarOpen,
    pfpUrl,
    searchOpen,
    setSearchOpen,
    deletingId,
    onConfirmDeleteRequest: (id) => setConfirmDeleteId(id)
  };

  if (!isAuth) return <Navigate to="/login" replace />;

  return (
    <>
      <Routes>
        <Route path="/new" element={
          <Layout {...sharedProps}>
            <NewChatPage onChatCreated={handleChatCreated} onTitleUpdate={updateChatTitle} selectedModel={currentModel} onModelChange={handleModelChange} onSearchOpen={() => setSearchOpen(true)} />
          </Layout>
        } />
        <Route path="/chat/:chatId" element={
          <Layout {...sharedProps}>
            <ChatPage chats={chats} onTitleUpdate={updateChatTitle} selectedModel={currentModel} onModelChange={handleModelChange} onStarChat={handleStarChat} onDeleteChat={deleteChat} onDeleteWithFade={handleDeleteWithFade} onConfirmDeleteRequest={(id) => setConfirmDeleteId(id)} onSearchOpen={() => setSearchOpen(true)} />
          </Layout>
        } />
        <Route path="/perfil" element={
          <Layout {...sharedProps}>
            <PerfilPage />
          </Layout>
        } />
        <Route path="*" element={<Navigate to="/new" replace />} />
      </Routes>

      {confirmDeleteId && (
        <Suspense fallback={null}>
          <ConfirmDeleteModal
            onConfirm={() => handleDeleteWithFade(confirmDeleteId)}
            onCancel={() => setConfirmDeleteId(null)}
          />
        </Suspense>
      )}
    </>
  );
}

export default App;
